import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from '../AddUserForm/AddUserForm.module.css'; // Reusing styles
import InputWithLabel from 'components/InputWithLabel/InputWithLabel';
import Button from 'components/Button/Button';
import { updateStudentSchema, updateTeacherSchema } from '../../types/userSchemas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserEdit,
    faAt,
    faArrowRight,
    faLayerGroup,
    faIdCard
} from '@fortawesome/free-solid-svg-icons';
import { api } from 'api/axios';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import Loading from 'components/Loading/Loading';

const EditUserForm: React.FC = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const role = searchParams.get('role') || 'student';
    
    const [loading, setLoading] = useState(true);
    const [username, setUsername] = useState('');
    const navigate = useNavigate();

    const isTeacher = role === 'teacher';
    const schema = isTeacher ? updateTeacherSchema : updateStudentSchema;

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<any>({
        resolver: zodResolver(schema),
    });



    useEffect(() => {
        const fetchUser = async () => {
            try {
                const endpoint = isTeacher ? `/teachers/${id}` : `/students/${id}`;
                const response = await api.get(endpoint);
                const user = response.data.data;
                setUsername(user.username);
                
                if (isTeacher) {
                    reset({
                        full_name: user.full_name,
                    });
                } else {
                    reset({
                        full_name: user.full_name,
                        grade: user.grade || '',
                        section: user.section || '',
                    });
                }
            } catch (error) {
                console.error(`Error fetching ${role}:`, error);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchUser();
    }, [id, reset, isTeacher, role]);

    const onSubmit = async (data: any) => {
        try {
            const finalData = data;
            const endpoint = isTeacher ? `/teachers/${id}` : `/students/${id}`;
            await api.patch(endpoint, finalData);
            alert(`${role.charAt(0).toUpperCase() + role.slice(1)} updated successfully!`);
            navigate('/user-management');
        } catch (error: any) {
            console.error(`Error updating ${role}:`, error);
            alert(error.response?.data?.message || `Failed to update ${role}`);
        }
    };

    if (loading) return <Loading fullPage message={`Fetching ${role} details...`} />;

    return (
        <div className={styles.pageContainer}>
            <div className={styles.formCard}>
                <header className={styles.header}>
                    <div className={styles.titleSection}>
                        <h1 className={styles.title}>Edit {role.charAt(0).toUpperCase() + role.slice(1)}</h1>
                        <p className={styles.subtitle}>Update {role} details for your educational institution.</p>
                    </div>
                    <div className={styles.userIcon}>
                        <FontAwesomeIcon icon={faUserEdit} />
                    </div>
                </header>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className={styles.grid}>


                        <InputWithLabel
                            label="Full Name"
                            placeholder="e.g. John Doe"
                            icon={<FontAwesomeIcon icon={faIdCard} />}
                            error={errors.full_name?.message as string}
                            {...register('full_name')}
                        />
                        <InputWithLabel
                            label="Username"
                            icon={<FontAwesomeIcon icon={faAt} />}
                            disabled
                            value={username}
                            readOnly
                        />

                        {!isTeacher && (
                            <>
                                <InputWithLabel
                                    label="Grade"
                                    placeholder="e.g. 10th"
                                    icon={<FontAwesomeIcon icon={faLayerGroup} />}
                                    error={errors.grade?.message as string}
                                    {...register('grade')}
                                />
                                <InputWithLabel
                                    label="Section"
                                    placeholder="e.g. A"
                                    icon={<FontAwesomeIcon icon={faIdCard} />}
                                    error={errors.section?.message as string}
                                    {...register('section')}
                                />
                            </>
                        )}
                    </div>

                    <div className={styles.actions}>
                        <Button
                            variant="primary"
                            type="submit"
                            className={styles.submitBtn}
                            isLoading={isSubmitting}
                        >
                            Update <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '10px' }} />
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditUserForm;
